/**
 * Permissions — SINGLE SOURCE OF TRUTH (Vincent #112 prévention drift)
 *
 * Origine : iter112 a découvert que `VideoDetailPage.js` avait DEUX définitions
 * divergentes du même concept (`isAdmin` étroit + `canEditKnowledge` large),
 * causant la disparition de blocs UI pour Etienne (concepteur). Cette classe
 * de bug ("duplicatas de concept qui divergent") avait déjà coûté :
 *   - iter109 : #364 mismatch `content_text` vs `text_content`
 *   - iter111 : #344 mismatch `quiz_attempts` vs `user_video_progress`
 *   - iter112 : Etienne mismatch `isAdmin` vs `canEditKnowledge`
 *
 * RÈGLE D'OR : tout nouveau check de permission frontend DOIT passer par
 * une de ces fonctions. Le backend test `test_permission_drift.py` vérifie
 * en CI que les whitelists ne divergent pas.
 *
 * Whitelist canonique (ALIGNÉE avec backend require_editor de core/auth.py) :
 *   - is_founder
 *   - is_super_admin
 *   - role === "admin"
 *   - org_role ∈ {founder, admin, ceo, president, vp, concepteur, employee_advanced}
 *   - access_level === "avance"
 */

/**
 * Peut éditer le contenu de la base de connaissances (Q&R, concepts,
 * procédures, scripts, dictionnaire, objections, transcriptions, locuteurs,
 * imports de fichiers texte/Fireflies/Q&R).
 *
 * Doit être RIGOUREUSEMENT identique à backend/core/auth.py::require_editor.
 */
export function canEditContent(user) {
  if (!user) return false;
  return !!(
    user.is_founder ||
    user.is_super_admin ||
    user.role === "admin" ||
    ["founder", "admin", "ceo", "president", "vp", "concepteur", "employee_advanced"].includes(user.org_role) ||
    user.access_level === "avance"
  );
}

/**
 * Peut accéder aux pages administratives STRICTES (Billing, Feature Flags,
 * Platform BYOK, Privacy/RGPD, Agora, Plateforme Dashboard).
 *
 * Plus restrictif que canEditContent : exclut concepteur et employee_advanced.
 * Aligné avec backend/core/auth.py::require_admin.
 */
export function canAccessAdmin(user) {
  if (!user) return false;
  return !!(
    user.is_founder ||
    user.is_super_admin ||
    user.role === "admin" ||
    ["founder", "admin", "ceo", "president", "vp"].includes(user.org_role)
  );
}

/**
 * Peut accéder aux pages PLATEFORME (multi-org) : Platform Dashboard,
 * Platform BYOK Overview, Audit Log global, Feature Flags, Bug Tracker, Agora.
 *
 * Plus restrictif que canAccessAdmin : réservé à l'équipe Knoria (fondateurs
 * et super-admins de la plateforme), pas aux admins d'une organisation
 * cliente. Aligné avec backend/core/auth.py::require_platform_admin.
 *
 * Eric 2026-06-25 (iter117) — ajout après détection de l'import manquant
 * introduit par le batch B1 d'iter116 (4 fichiers cassaient la compile).
 */
export function canAccessPlatform(user) {
  if (!user) return false;
  return !!(
    user.is_founder ||
    user.is_super_admin ||
    user.platform_role === "ROOT_OWNER"
  );
}

/**
 * Tâche 1443 (Eric 2026-10-02) — Peut concevoir du contenu : page Orthographe.
 *
 * Portée CONFIRMÉE par Eric : **concepteur, admin et fondateur uniquement**.
 * Plus étroit que `canEditContent` : `employee_advanced` et
 * `access_level === "avance"` sont exclus **même en lecture**.
 * Aligné avec backend/core/auth.py::require_designer.
 */
export function canDesignContent(user) {
  if (!user) return false;
  return !!(
    user.is_founder ||
    user.is_super_admin ||
    user.role === "admin" ||
    DESIGNER_ORG_ROLES.includes(user.org_role)
  );
}

/**
 * Liste canonique des org_role autorisés à concevoir (page Orthographe).
 */
export const DESIGNER_ORG_ROLES = ["founder", "admin", "ceo", "president", "vp", "concepteur"];

/**
 * Liste canonique des org_role autorisés à éditer le contenu (sans access_level).
 * Utilisée pour la détection de drift et les unit tests.
 */
export const EDITOR_ORG_ROLES = ["founder", "admin", "ceo", "president", "vp", "concepteur", "employee_advanced"];

/**
 * Liste canonique des org_role autorisés à accéder à l'admin strict.
 */
export const ADMIN_ORG_ROLES = ["founder", "admin", "ceo", "president", "vp"];
// iter115 test marker
